import { useState, useEffect } from 'react'
import { Building2, Plus, Pencil, Check, X, MapPin, Phone, Clock, Settings, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

interface OpeningDay { open: string; close: string; closed: boolean }
interface OpeningHours { mon: OpeningDay; tue: OpeningDay; wed: OpeningDay; thu: OpeningDay; fri: OpeningDay; sat: OpeningDay; sun: OpeningDay }
interface BadgeSettings { roundingMode: string; lateToleranceMin: number; autoBreakAfterMin: number; autoBreakDurationMin: number; alertOverMin: number; allowMobileClocking: boolean }
interface PlanningSettings { minRestBetweenShiftsH: number; maxConsecutiveDays: number; maxDailyAmplitudeH: number; lockPastPlanning: boolean }

interface Establishment {
  id: string; name: string; address?: string; zipCode?: string; city?: string; phone?: string
  internalCode?: string; badgeEnabled: boolean
  openingHours?: OpeningHours
  badgeSettings?: BadgeSettings
  planningSettings?: PlanningSettings
  localRules?: any
  latitude?: number | null
  longitude?: number | null
}

const DAY_LABELS: Record<string, string> = { mon: 'Lundi', tue: 'Mardi', wed: 'Mercredi', thu: 'Jeudi', fri: 'Vendredi', sat: 'Samedi', sun: 'Dimanche' }
const DAYS = ['mon','tue','wed','thu','fri','sat','sun'] as const

const DEFAULT_OPENING: OpeningHours = {
  mon: { open: '07:00', close: '21:00', closed: false },
  tue: { open: '07:00', close: '21:00', closed: false },
  wed: { open: '07:00', close: '21:00', closed: false },
  thu: { open: '07:00', close: '21:00', closed: false },
  fri: { open: '07:00', close: '21:00', closed: false },
  sat: { open: '07:00', close: '21:00', closed: false },
  sun: { open: '09:00', close: '13:00', closed: false },
}

const DEFAULT_BADGE: BadgeSettings = {
  roundingMode: '15min', lateToleranceMin: 5, autoBreakAfterMin: 360,
  autoBreakDurationMin: 30, alertOverMin: 60, allowMobileClocking: true
}

const DEFAULT_PLANNING: PlanningSettings = {
  minRestBetweenShiftsH: 11, maxConsecutiveDays: 6, maxDailyAmplitudeH: 13, lockPastPlanning: false
}

const emptyForm = {
  name: '', address: '', zipCode: '', city: '', phone: '', internalCode: '',
  badgeEnabled: true,
  openingHours: DEFAULT_OPENING,
  badgeSettings: DEFAULT_BADGE,
  planningSettings: DEFAULT_PLANNING,
  latitude: '' as string | number,
  longitude: '' as string | number,
}

type Tab = 'info' | 'horaires' | 'badgeuse' | 'planning'

export default function Establishments() {
  const { establishments: ctxEstabs, current, setCurrent } = useEstablishment()
  const [establishments, setEstablishments] = useState<Establishment[]>([])
  const [modal, setModal] = useState<{ open: boolean; estab?: Establishment }>({ open: false })
  const [form, setForm] = useState(emptyForm)
  const [tab, setTab] = useState<Tab>('info')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    const r = await api.get('/establishments')
    setEstablishments(r.data)
  }

  function openCreate() {
    setForm(emptyForm)
    setTab('info')
    setError('')
    setModal({ open: true })
  }

  function openEdit(e: Establishment) {
    setForm({
      name: e.name, address: e.address || '', zipCode: e.zipCode || '',
      city: e.city || '', phone: e.phone || '', internalCode: e.internalCode || '',
      badgeEnabled: e.badgeEnabled,
      openingHours: e.openingHours || DEFAULT_OPENING,
      badgeSettings: e.badgeSettings || DEFAULT_BADGE,
      planningSettings: e.planningSettings || DEFAULT_PLANNING,
      latitude: e.latitude ?? '',
      longitude: e.longitude ?? '',
    })
    setTab('info')
    setError('')
    setModal({ open: true, estab: e })
  }

  async function save() {
    if (!form.name.trim()) { setError('Le nom est requis'); return }
    setSaving(true)
    setError('')
    try {
      if (modal.estab) {
        await api.put(`/establishments/${modal.estab.id}`, form)
      } else {
        await api.post('/establishments', form)
      }
      await load()
      window.location.reload() // rafraîchit le contexte
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur')
      setSaving(false)
    }
  }

  function setDay(day: string, field: keyof OpeningDay, value: string | boolean) {
    setForm(f => ({
      ...f,
      openingHours: {
        ...f.openingHours,
        [day]: { ...f.openingHours[day as keyof OpeningHours], [field]: value }
      }
    }))
  }

  const displayed = establishments.length > 0 ? establishments : (ctxEstabs as any[])

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Établissements</h1>
          <span className="text-sm text-gray-400">{displayed.length} établissement{displayed.length > 1 ? 's' : ''}</span>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          <Plus size={15} /> Nouvel établissement
        </button>
      </div>

      <div className="grid gap-4 max-w-3xl">
        {displayed.map((e: Establishment) => (
          <div key={e.id}
            className={`bg-white rounded-xl border-2 p-5 transition-colors ${current?.id === e.id ? 'border-blue-500' : 'border-gray-200'}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${current?.id === e.id ? 'bg-blue-600' : 'bg-gray-100'}`}>
                  <Building2 size={18} className={current?.id === e.id ? 'text-white' : 'text-gray-500'} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-gray-900">{e.name}</h2>
                    {current?.id === e.id && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Actif</span>
                    )}
                    {e.internalCode && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-mono">{e.internalCode}</span>
                    )}
                  </div>
                  {(e.address || e.city) && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                      <MapPin size={13} />
                      <span>{[e.address, e.zipCode, e.city].filter(Boolean).join(' ')}</span>
                    </div>
                  )}
                  {e.phone && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                      <Phone size={13} /><span>{e.phone}</span>
                    </div>
                  )}
                  {/* Résumé horaires */}
                  {e.openingHours && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1.5">
                      <Clock size={11} />
                      <span>
                        {DAYS.filter(d => !e.openingHours![d].closed)
                          .map(d => DAY_LABELS[d].slice(0,3))
                          .join(' · ')}
                        {' '}— {e.openingHours.mon.open}–{e.openingHours.mon.close}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {current?.id !== e.id && (
                  <button onClick={() => setCurrent(e as any)}
                    className="text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors font-medium">
                    Sélectionner
                  </button>
                )}
                <button onClick={() => openEdit(e)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <Pencil size={15} />
                </button>
                <button onClick={async () => { if (confirm(`Supprimer "${e.name}" ?`)) { await api.delete(`/establishments/${e.id}`); setEstablishments(prev => prev.filter(x => x.id !== e.id)) } }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 max-w-3xl bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
        <p className="font-semibold mb-1">Multi-établissements</p>
        <p>Chaque établissement a ses propres employés, plannings, horaires et paramètres. Une barre de sélection apparaît en haut de chaque page dès que vous avez plusieurs établissements.</p>
      </div>

      {/* Modal */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h2 className="text-base font-bold text-gray-900">
                {modal.estab ? `Modifier — ${modal.estab.name}` : 'Nouvel établissement'}
              </h2>
              <button onClick={() => setModal({ open: false })} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 flex-shrink-0">
              {(['info','horaires','badgeuse','planning'] as Tab[]).map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors capitalize ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t === 'info' ? 'Infos' : t === 'horaires' ? 'Horaires' : t === 'badgeuse' ? 'Badgeuse' : 'Planning'}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

              {/* ── Tab Infos ── */}
              {tab === 'info' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="ex: Utile Cassis Centre"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Code interne</label>
                      <input value={form.internalCode} onChange={e => setForm(f => ({ ...f, internalCode: e.target.value }))}
                        placeholder="ex: MAG-001"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                      <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="04 42 XX XX XX"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
                      <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                        placeholder="12 rue du Commerce"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Code postal</label>
                      <input value={form.zipCode} onChange={e => setForm(f => ({ ...f, zipCode: e.target.value }))}
                        placeholder="13260"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Ville</label>
                      <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                        placeholder="Cassis"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Latitude GPS</label>
                      <input type="number" step="0.000001" value={form.latitude}
                        onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))}
                        placeholder="ex: 48.8566"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Longitude GPS</label>
                      <input type="number" step="0.000001" value={form.longitude}
                        onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))}
                        placeholder="ex: 2.3522"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div className="col-span-2 text-xs text-gray-400 -mt-2">
                      Coordonnées GPS requises pour afficher la météo sur le planning.
                      <a href="https://www.latlong.net" target="_blank" rel="noreferrer" className="ml-1 text-blue-500 underline">Trouver mes coordonnées</a>
                    </div>
                    <div className="col-span-2 flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Badgeuse activée</p>
                        <p className="text-xs text-gray-500">Les employés peuvent pointer depuis cet établissement</p>
                      </div>
                      <button type="button" onClick={() => setForm(f => ({ ...f, badgeEnabled: !f.badgeEnabled }))}
                        className={`transition-colors ${form.badgeEnabled ? 'text-blue-600' : 'text-gray-400'}`}>
                        {form.badgeEnabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── Tab Horaires ── */}
              {tab === 'horaires' && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-3">Horaires d'ouverture du magasin — utilisés comme référence pour le planning et la badgeuse.</p>
                  {DAYS.map(day => {
                    const d = form.openingHours[day]
                    return (
                      <div key={day} className={`flex items-center gap-4 p-3 rounded-xl border ${d.closed ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200'}`}>
                        <div className="w-24 text-sm font-medium text-gray-700">{DAY_LABELS[day]}</div>
                        <button type="button" onClick={() => setDay(day, 'closed', !d.closed)}
                          className={`transition-colors ${!d.closed ? 'text-blue-600' : 'text-gray-300'}`}>
                          {!d.closed ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
                        </button>
                        {!d.closed ? (
                          <>
                            <input type="time" value={d.open} onChange={e => setDay(day, 'open', e.target.value)}
                              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                            <span className="text-gray-400 text-sm">—</span>
                            <input type="time" value={d.close} onChange={e => setDay(day, 'close', e.target.value)}
                              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                          </>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Fermé</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* ── Tab Badgeuse ── */}
              {tab === 'badgeuse' && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500">Paramètres spécifiques à cet établissement — remplacent les paramètres globaux.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Arrondi pointage</label>
                      <select value={form.badgeSettings.roundingMode}
                        onChange={e => setForm(f => ({ ...f, badgeSettings: { ...f.badgeSettings, roundingMode: e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                        <option value="none">Aucun (à la minute)</option>
                        <option value="5min">5 minutes</option>
                        <option value="10min">10 minutes</option>
                        <option value="15min">15 minutes</option>
                        <option value="30min">30 minutes</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tolérance retard (min)</label>
                      <input type="number" min={0} max={60} value={form.badgeSettings.lateToleranceMin}
                        onChange={e => setForm(f => ({ ...f, badgeSettings: { ...f.badgeSettings, lateToleranceMin: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Pause auto après (min de travail)</label>
                      <input type="number" min={0} value={form.badgeSettings.autoBreakAfterMin}
                        onChange={e => setForm(f => ({ ...f, badgeSettings: { ...f.badgeSettings, autoBreakAfterMin: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Durée pause auto (min)</label>
                      <input type="number" min={0} value={form.badgeSettings.autoBreakDurationMin}
                        onChange={e => setForm(f => ({ ...f, badgeSettings: { ...f.badgeSettings, autoBreakDurationMin: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Alerte dépassement (min)</label>
                      <input type="number" min={0} value={form.badgeSettings.alertOverMin}
                        onChange={e => setForm(f => ({ ...f, badgeSettings: { ...f.badgeSettings, alertOverMin: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Pointage mobile</p>
                        <p className="text-xs text-gray-500">Via smartphone</p>
                      </div>
                      <button type="button"
                        onClick={() => setForm(f => ({ ...f, badgeSettings: { ...f.badgeSettings, allowMobileClocking: !f.badgeSettings.allowMobileClocking } }))}
                        className={`transition-colors ${form.badgeSettings.allowMobileClocking ? 'text-blue-600' : 'text-gray-400'}`}>
                        {form.badgeSettings.allowMobileClocking ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab Planning ── */}
              {tab === 'planning' && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500">Règles de planning spécifiques à cet établissement.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Repos minimum entre 2 shifts (h)</label>
                      <input type="number" min={0} max={24} value={form.planningSettings.minRestBetweenShiftsH}
                        onChange={e => setForm(f => ({ ...f, planningSettings: { ...f.planningSettings, minRestBetweenShiftsH: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Jours consécutifs max</label>
                      <input type="number" min={1} max={7} value={form.planningSettings.maxConsecutiveDays}
                        onChange={e => setForm(f => ({ ...f, planningSettings: { ...f.planningSettings, maxConsecutiveDays: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Amplitude journalière max (h)</label>
                      <input type="number" min={1} max={24} value={form.planningSettings.maxDailyAmplitudeH}
                        onChange={e => setForm(f => ({ ...f, planningSettings: { ...f.planningSettings, maxDailyAmplitudeH: +e.target.value } }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Verrouiller le passé</p>
                        <p className="text-xs text-gray-500">Interdit de modifier un shift passé</p>
                      </div>
                      <button type="button"
                        onClick={() => setForm(f => ({ ...f, planningSettings: { ...f.planningSettings, lockPastPlanning: !f.planningSettings.lockPastPlanning } }))}
                        className={`transition-colors ${form.planningSettings.lockPastPlanning ? 'text-blue-600' : 'text-gray-400'}`}>
                        {form.planningSettings.lockPastPlanning ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 flex-shrink-0">
              <button onClick={() => setModal({ open: false })}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                Annuler
              </button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg transition-colors">
                <Check size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
